// SPEC-LAUNCHER-UPDATE-NOTICE-01 (D-982): ログイン後ハブ(Launcher)に出す「新しいバージョンがあります」通知。
//   ひろ GO (2026-10-02 23:29 JST): タップで更新のみ。SW の自動リロード経路を足さない(.claude/rules/command-tower.md 末尾)。
//   ★状態源は既存の2本だけを使う(新しい SW 検知ロジックは作らない):
//     ・subscribeNeedRefresh / applyUpdate (src/lib/swRegistration.js) … waiting SW の実在(押せば効く)
//     ・fetchRemoteVersion (同) … version.json の sha 比較(waiting 未準備でも「新版あり」を知るための補助)
//   resolveNoticeState は VersionPanel.jsx の resolveUpdateAction と同じ考え方(waiting 優先)を
//   この画面向けに写した純関数。VersionPanel.jsx / UpdateTips.jsx 自体は D-463/D-661 で変更禁止のため
//   import はせず、同じ判定を独立に持つ(ログイン画面専用の forced/lockout ロジックはここに持ち込まない=
//   D-456 ITEM_6 のコメント通りログイン後の作業中には強制更新をかけない)。
//
//   未送信ガード: 巡回/集金のどちらかに未送信があれば、即適用せず一度確認を挟む
//   (データロスの危険を避ける。安全 > スピード)。
//
//   ★D-982c (gate_3 差し戻し是正・2回目): D-982b で導入した getUnsyncedSummary/getUnsyncedDraftSummary
//   は「副作用なし」ではあったが、内部で呼ぶ getUnsyncedRecords/getAllUnsyncedDrafts が IDB 例外を
//   catch して [] を返す実装のため、getDb() 失敗や IDB 障害が「未送信ゼロ」に化けて fail-open していた
//   (checkUnsentStatus の timeout/catch 二重防御はコンポーネント内の話で、関数自体が catch 済みで
//   reject しないため発火しなかった)。D-982b のテストはこの2関数を直接 mock で reject させていたため、
//   実物では起きないシナリオで green になっていた。
//   → catch を持たない strict 版(countUnsyncedRecordsStrict / countUnsyncedDraftsStrict、
//   src/lib/localStore/patrolRecords.js・collectionDrafts.js)を新設し、既定の DI 先をそちらへ切替えた。
//   既存関数(getUnsyncedSummary 等)・既存呼び出し元(useUnsentBanner 等)は 1 バイトも変えていない。
//
//   ★D-982b (gate_3 差し戻し是正):
//   (a) 未送信件数は「タップした瞬間」に数え直す。useUnsentBanner/useCollectionUnsent の
//       state を信じない — 両 hook は count=0 で始まり集計が非同期なため、マウント直後に
//       タップされると「未確認」が「未送信ゼロ」と誤認され確認なしで適用されてしまう
//       (fail-open)。加えて読み取りが失敗/タイムアウトした場合も「未送信ゼロ」に
//       化けてはならない → checkUnsentStatus() が 'unknown' を返し、確認画面を出す側に倒す。
//   (b) useUnsentBanner/useCollectionUnsent はこのコンポーネントでは一切マウントしない
//       (App.jsx の UnsentBanner/CollectionUnsentBanner が既にマウント済み)。
//       ここで再マウントすると各 hook の副作用
//       (deleteOrphanedNullStoreRecords の IDB 削除、runAlarmAndStorage の Sentry 警報+
//        dedup 読み書き)が二重実行されるため、件数だけを返す純粋な読み取り関数
//       (D-982c 以降は strict 版 countUnsyncedRecordsStrict / countUnsyncedDraftsStrict、
//        いずれも副作用なし=上の D-982c コメント参照) を タップ時に直接呼ぶ。
//   (c) 1回タップしたら確認完了まで再タップで二重に applyFn が走らないようにする
//       (appliedRef ガード + ボタン disabled「更新中…」)。
//
//   ★自動適用は絶対にしない。更新の適用やページ再読込は
//     ユーザーのタップ由来のイベントハンドラ内だけで呼ぶ(useEffect/マウント時には絶対に呼ばない)。
//     これは src/__tests__/components/updateNoticeD982.test.jsx の source-contract テストで担保する。
import { LinkChevron } from '../shared/ui/icons/ChevronIcons'
import { useCallback, useEffect, useRef, useState } from 'react'
import { subscribeNeedRefresh, applyUpdate, fetchRemoteVersion, subscribeInstallState, retryInstall } from '../lib/swRegistration'
import { BUILD_SHA } from '../lib/buildInfo'
// D-982c: 件数だけを返す「厳格(strict)」な読み取り関数(副作用なし・catch なしで例外を伝播する):
//   - countUnsyncedRecordsStrict (src/lib/localStore/patrolRecords.js) … getDb() + bySynced index を
//     直接読む。既存の getUnsyncedRecords/getUnsyncedSummary のような catch→[] 変換を一切挟まない。
//   - countUnsyncedDraftsStrict (src/lib/localStore/collectionDrafts.js) … 同様に getDb() + bySynced
//     index を直接読み、isSendableDraft(status==='complete')で絞るだけ。
//   書き込み/削除/Sentry 送信は一切しない(いずれも読み取りのみ)。
import { countUnsyncedRecordsStrict } from '../lib/localStore/patrolRecords'
import { countUnsyncedDraftsStrict } from '../lib/localStore/collectionDrafts'

// D-982b/c: 2つの件数読み取りを並行実行し、タイムアウト/例外は 'unknown' として返す
//   (fail-safe: 確認できない = 未送信「あり」と同じ扱いで確認画面に倒す)。
//   D-982c: 既定の getPatrolUnsent/getCollectionUnsent は strict 版(catch なしで reject する)なので
//   この try/catch が実際に効く。timeout は IDB ハング等、catch 一本だけでは防げないケースの保険。
export const UNSENT_CHECK_TIMEOUT_MS = 3000
export async function checkUnsentStatus({ getPatrolUnsent, getCollectionUnsent, timeoutMs = UNSENT_CHECK_TIMEOUT_MS }) {
  let timer
  try {
    const timeout = new Promise((resolve) => { timer = setTimeout(() => resolve('__timeout__'), timeoutMs) })
    const result = await Promise.race([
      Promise.all([getPatrolUnsent(), getCollectionUnsent()]),
      timeout,
    ])
    if (result === '__timeout__') return 'unknown'
    const [patrol, collection] = result
    const count = (patrol?.count ?? 0) + (collection?.count ?? 0)
    return count > 0 ? 'has' : 'none'
  } catch {
    return 'unknown'
  } finally {
    clearTimeout(timer)
  }
}

// 純関数: waiting(SW waiting の実在) と badge(version.json 由来の sha 差) から表示状態を決める。
//   apply     … waiting あり。タップすれば即効く。
//   preparing … version.json は新版を示すが waiting 未準備(VersionPanel と同じ「準備中」概念)。
//   failed    … D-1041: 新しい SW の install が失敗した(redundant)。「もう一度準備する」を出す。
//   hidden    … 新版なし。
//   install は swRegistration の install 状態('idle'|'installing'|'installed'|'failed')。
export function resolveNoticeState({ waiting, badge, install = 'idle' }) {
  if (waiting) return 'apply'
  if (install === 'failed') return 'failed'
  if (badge === 'update-available' || install === 'installing') return 'preparing'
  return 'hidden'
}

export default function UpdateNotice({
  subscribeFn = subscribeNeedRefresh,
  applyFn = applyUpdate,
  fetchVersionFn = fetchRemoteVersion,
  currentSha = BUILD_SHA,
  getPatrolUnsentFn = countUnsyncedRecordsStrict,
  getCollectionUnsentFn = countUnsyncedDraftsStrict,
  unsentCheckTimeoutMs = UNSENT_CHECK_TIMEOUT_MS,
  installSubscribeFn = subscribeInstallState, // D-1041: SW の install の状態
  retryFn = retryInstall,                     // D-1041:「もう一度準備する」= registration.update() だけ
}) {
  const [waiting, setWaiting] = useState(false)
  const [install, setInstall] = useState('idle') // D-1041
  const [badge, setBadge] = useState('idle')
  // D-982b: phase は 'idle' | 'checking' | 'confirm-unsent' | 'confirm-unknown'。
  //   未送信件数はここでは state に保持しない(タップ時に checkUnsentStatus で都度確認する)。
  const [phase, setPhase] = useState('idle')
  // 二重タップ/二重 applyFn 防止(c)。state ではなく ref: 再レンダリングの隙間でも即座に効く。
  const appliedRef = useRef(false)

  // waiting の購読のみ。既存の集約層(swRegistration)が15分おき/visibilitychangeで能動チェックし、
  // waiting 化した瞬間にこの購読へ push される。ここで新しいポーリングは作らない。
  useEffect(() => {
    const unsub = subscribeFn(setWaiting)
    return () => { if (typeof unsub === 'function') unsub() }
  }, [subscribeFn])

  // D-1041: install の状態の購読(見せるだけ。適用・再読込はしない)。
  useEffect(() => {
    const unsub = installSubscribeFn((st) => setInstall(st?.phase || 'idle'))
    return () => { if (typeof unsub === 'function') unsub() }
  }, [installSubscribeFn])

  // マウント時に1回だけ version.json を見て「新版あり(waiting 未準備)」の badge を立てる。
  //   取得失敗は badge を変えない(=hidden のまま。確認できなかったことを通知として出さない)。
  useEffect(() => {
    let alive = true
    Promise.resolve().then(fetchVersionFn).then((r) => {
      if (!alive) return
      if (r?.sha && r.sha !== currentSha) setBadge('update-available')
    }).catch(() => {})
    return () => { alive = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const action = resolveNoticeState({ waiting, badge, install })

  // 更新の適用。ここだけが applyFn を呼ぶ(ハンドラ由来のみ、useEffect からは呼ばない=source-contract)。
  const doApply = useCallback(() => {
    if (appliedRef.current) return
    appliedRef.current = true
    applyFn()
  }, [applyFn])

  // タップ由来のハンドラ。未送信は「タップした今」確認する(表示中の数を信じない=(a))。
  const handleTap = useCallback(() => {
    if (action !== 'apply') return
    if (phase === 'checking' || appliedRef.current) return // 二重タップ防止(c)
    setPhase('checking')
    checkUnsentStatus({
      getPatrolUnsent: getPatrolUnsentFn,
      getCollectionUnsent: getCollectionUnsentFn,
      timeoutMs: unsentCheckTimeoutMs,
    }).then((status) => {
      if (status === 'none') { doApply(); return }
      setPhase(status === 'unknown' ? 'confirm-unknown' : 'confirm-unsent')
    })
  }, [action, phase, getPatrolUnsentFn, getCollectionUnsentFn, unsentCheckTimeoutMs, doApply])

  // 確認画面「それでも更新する」。ここは再チェックせず即 doApply(何度も IDB を往復させない)。
  const handleConfirmOk = useCallback(() => { doApply() }, [doApply])
  const handleCancel = useCallback(() => setPhase('idle'), [])

  // D-1041:「もう一度準備する」。retryFn(= registration.update() だけ)を呼ぶ。applyFn/doApply は呼ばない。
  const handleRetry = useCallback(() => { try { Promise.resolve(retryFn()).catch(() => {}) } catch { /* 表示だけの導線。失敗しても画面は止めない */ } }, [retryFn])

  if (action === 'hidden') return null

  if (action === 'failed') {
    return (
      <div
        data-testid="update-notice-install-failed"
        role="status"
        aria-live="polite"
        className="w-full flex flex-col gap-2 px-4 py-3 rounded-xl border border-amber-500/60 bg-amber-500/10"
      >
        <p className="text-sm text-amber-200">新しい版の準備に失敗しました。電波の良い所で「もう一度準備する」を押してください</p>
        <button
          type="button"
          data-testid="update-notice-retry"
          onClick={handleRetry}
          className="w-full min-h-[44px] rounded-lg border border-border text-sm font-bold text-text active:bg-bg/40"
        >もう一度準備する</button>
      </div>
    )
  }

  if (action === 'preparing') {
    // D-1041 (3): 準備中は「この画面を開いたまま待つ」を先に案内する(閉じると取り込みが中断されうる)。
    return (
      <div
        data-testid="update-notice-preparing"
        data-install-phase={install}
        role="status"
        aria-live="polite"
        className="w-full flex items-center gap-2 px-4 py-2 rounded-xl border border-border bg-surface text-muted text-sm min-h-[44px]"
      >
        <span aria-hidden>🔄</span>
        <span>
          {install === 'installing' ? '新しい版を読み込み中… ' : '更新の準備中… '}
          この画面を開いたまま少し待ってください。長引くときはアプリを閉じて開き直してください
        </span>
      </div>
    )
  }

  // action === 'apply'
  if (phase === 'confirm-unsent' || phase === 'confirm-unknown') {
    // (a) 'unknown'(タイムアウト/例外で未送信件数を確認できなかった)も「ゼロ」扱いにせず、
    //     確認を挟む側に倒す。文言を変えて利用者に状況を伝える。
    const message = phase === 'confirm-unknown'
      ? '未送信の確認ができませんでした。残っている場合、このまま更新すると失われるおそれがあります。'
      : '未送信のデータがあります。先に送信してから更新することをおすすめします。'
    return (
      <div
        data-testid="update-notice-confirm"
        role="alertdialog"
        aria-live="polite"
        className="w-full flex flex-col gap-2 px-4 py-3 rounded-xl border border-amber-500/60 bg-amber-500/10"
      >
        <p className="text-sm text-amber-200">{message}</p>
        <div className="flex gap-2">
          <button
            type="button"
            data-testid="update-notice-confirm-cancel"
            onClick={handleCancel}
            className="flex-1 min-h-[44px] rounded-lg border border-border text-sm text-text active:bg-bg/40"
          >
            キャンセル
          </button>
          <button
            type="button"
            data-testid="update-notice-confirm-ok"
            onClick={handleConfirmOk}
            className="flex-1 min-h-[44px] rounded-lg text-sm font-bold active:opacity-80"
            style={{ background: '#f0c040', color: '#1a1a1a' }}
          >
            それでも更新する
          </button>
        </div>
      </div>
    )
  }

  // phase === 'idle' | 'checking'
  const checking = phase === 'checking'
  return (
    <button
      type="button"
      data-testid="update-notice-apply"
      onClick={handleTap}
      disabled={checking}
      aria-label="新しいバージョンがあります。タップで更新"
      className="w-full flex items-center justify-between gap-2 px-4 py-2 rounded-xl min-h-[44px] font-bold text-sm active:opacity-90 disabled:opacity-60"
      style={{ background: '#f0c040', color: '#1a1a1a' }}
    >
      <span>新しいバージョンがあります</span>
      <span>{checking ? '更新中…' : <>タップで更新<LinkChevron /></>}</span>
    </button>
  )
}
